import React, { useEffect, useRef, useState } from "react";
import { prefersReducedMotion } from "../lib/motion";

/**
 * Scroll-triggered entrance. Variants are defined in index.css:
 *  - "up"    fade + rise
 *  - "fade"  opacity only
 *  - "clip"  wipe upward (used for images)
 *  - "lines" children wrapped in `.mask-line` slide up from a mask
 * Hover/transition styles belong on children, not on the Reveal element itself.
 */
const Reveal = ({ children, delay = 0, className = "", as: Tag = "div", variant = "up", style, ...rest }) => {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const node = ref.current;
        if (!node) return;

        if (prefersReducedMotion() || typeof IntersectionObserver === "undefined") {
            setVisible(true);
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.12, rootMargin: "0px 0px -6% 0px" }
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    return (
        <Tag
            ref={ref}
            data-reveal={variant}
            data-visible={visible}
            className={className}
            style={{ "--reveal-delay": `${delay}ms`, ...style }}
            {...rest}
        >
            {/* The clip lives on an inner layer: a fully clipped target never reports as intersecting. */}
            {variant === "clip" ? <div className="reveal-clip">{children}</div> : children}
        </Tag>
    );
};

export default Reveal;
